import React from 'react';
import Form, { FormItemProps, Rule } from 'antd/es/form';
import styled from 'styled-components';
import { FormListFieldData } from 'antd/es/form/FormList';
import Typography from 'antd/es/typography';

export const InputDescription = styled(Typography)`
    margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

// extending FormListFieldData in order to allow text inputs to be used in Form.List
export interface BaseInputProps extends Partial<Omit<FormListFieldData, 'name' | 'key'>> {
    label?: string;
    name?: string | number;
    rules?: { [key: string]: any }[];
    description?: string;
    placeholder?: string;
    hidden?: boolean;
}

interface CustomFormItemProps extends FormItemProps {
    labelRules?: Rule[];
}
export const FormItem = (props: CustomFormItemProps) => {
    const { children, label, labelRules, ...rest } = props;
    const showRequiredMark = !!(
        label &&
        labelRules?.length &&
        labelRules.some((rule) => (rule as any).required)
    );
    /**
     * Complex forms (like the one we have) sometimes dictate that
     * the form.item with the 'label' is separated from the form.item
     * with the actual input component.
     * This separation prevents AntD from knowing how to put a required
     * mark on the label since the label doesn't know about the rules.
     * We add the 'labelRules' prop so we can re-introduce automatic required marks
     * to labeled form items.
     */
    // we also are correcting for ant-d's inability to show the label colon
    // for vertical layouts.
    return (
        <Form.Item label={label ? `${label}:` : undefined} required={showRequiredMark} {...rest}>
            {children}
        </Form.Item>
    );
};

interface DescriptionFormItemProps extends FormItemProps {
    description: string;
}
/**
 * Component that adds an input description under the label
 * and also optionally allows for input children to be passed
 */
export const DescriptionFormItem = ({
    label,
    name,
    rules,
    description,
    children,
    hidden,
}: DescriptionFormItemProps) => {
    return (
        <FormItem label={label} key={`${name}`} labelRules={rules} hidden={hidden}>
            {description && <InputDescription>{description}</InputDescription>}
            {children && (
                <FormItem noStyle name={name} rules={rules}>
                    {children}
                </FormItem>
            )}
        </FormItem>
    );
};
